import { inject, provideAppInitializer } from '@angular/core';
import { LoginService } from '@uoa/auth';
import { ExternalLoginService } from '../services/external-login.service';

/**
 * Replaces @uoa/auth's internal refresh (AuthService._exchangeRefreshTokenForTokens) with
 * ExternalLoginService.refreshSession().
 *
 * Every @uoa/auth path that renews an expired access token - AuthGuard (via
 * LoginService.doLogin()) and its AuthInterceptor on every API call - funnels into that one
 * private method, which on ANY failure wipes all tokens and redirects to the Cognito Hosted
 * UI. Confirmed live on dev: logged in online, token expired, went offline, opened "Enter
 * Line Check (Offline)" -> POST oauth2/token 504 -> tokens wiped -> browser sent to the
 * Hosted UI's authorize URL (Chrome's offline page). So the page built for no-signal use
 * logged field users out.
 *
 * With this in place a network failure keeps the tokens: obtainValidAccessToken() goes on
 * to return the stored (expired) access token, the guard lets the user in, and API calls
 * fail as ordinary offline requests. A refresh token Cognito actually rejects is still
 * cleared, so the library falls through to its normal "no token" login redirect.
 *
 * AuthService isn't exported by @uoa/auth, so it's reached through LoginService's private
 * reference - the same approach main.ts already takes patching window.open for this library.
 * If a library upgrade renames either member, this logs an error and leaves the original
 * behaviour in place rather than breaking login.
 */
interface UoaAuthServiceInternals {
  _exchangeRefreshTokenForTokens?: (refreshToken: string) => Promise<void>;
}

export function provideOfflineSafeRefresh() {
  return provideAppInitializer(() => {
    const authService = (inject(LoginService) as unknown as { _authService?: UoaAuthServiceInternals })._authService;
    const externalLoginService = inject(ExternalLoginService);
    if (!authService || typeof authService._exchangeRefreshTokenForTokens !== 'function') {
      console.error('@uoa/auth internals changed - offline-safe token refresh not installed.');
      return;
    }
    authService._exchangeRefreshTokenForTokens = async (): Promise<void> => {
      await externalLoginService.refreshSession();
    };
  });
}
